import React, { useState, useCallback, useEffect } from 'react';
import { StringComboBox } from 'components/inputs/StringComboBox';
import { IDateRangeFilterLayerProps } from './DateRangeFilterLayer';
import { RollingDateRangeType } from '@decisions/server/DecisionsFramework/Design/Flow/CoreSteps/Metro/RollingDateRangeType';
import { RollingDateRangeTimePeriodType } from '@decisions/server/DecisionsFramework/Design/Flow/CoreSteps/Metro/RollingDateRangeTimePeriodType';
import { newReportDateRange } from '@decisions/server/DecisionsFramework/Design/Flow/CoreSteps/Metro/ReportDateRange';
import { DateRangeTypes } from '@decisions/server/DecisionsFramework/Design/Flow/CoreSteps/Metro/DateRangeTypes';

export function RollingDateFilter({ dateRangeFilterData, onSelectionChange }: IDateRangeFilterLayerProps) {
  const [rollingPeriod, setRollingPeriod] = useState(dateRangeFilterData?.RollingPeriod);
  const [rollingDateRangeTimePeriodType, setRollingDateRangeTimePeriodType] = useState(
    dateRangeFilterData?.RollingDateRangeTimePeriodType
  );
  const [rollingDateRangeType, setRollingDateRangeType] = useState(dateRangeFilterData?.RollingDateRangeType);

  useEffect(() => {
    setRollingPeriod(dateRangeFilterData?.RollingPeriod);
    setRollingDateRangeTimePeriodType(dateRangeFilterData?.RollingDateRangeTimePeriodType);
    setRollingDateRangeType(dateRangeFilterData?.RollingDateRangeType);
  }, [
    dateRangeFilterData?.RollingDateRangeTimePeriodType,
    dateRangeFilterData?.RollingDateRangeType,
    dateRangeFilterData?.RollingPeriod,
  ]);

  const raiseSelectionChange = useCallback(
    (dateRangeType?: RollingDateRangeType, period?: number, timePeriodType?: RollingDateRangeTimePeriodType) => {
      onSelectionChange(
        newReportDateRange({
          ...dateRangeFilterData,
          DateRange: DateRangeTypes.Rolling,
          RollingDateRangeType: dateRangeType,
          RollingDateRangeTimePeriodType: timePeriodType,
          RollingPeriod: period,
        })
      );
    },
    [dateRangeFilterData, onSelectionChange]
  );

  const onRollingDateRangeTypeChange = useCallback(
    (selectedItem: RollingDateRangeType | null) => {
      let value;
      if (selectedItem) value = selectedItem;
      setRollingDateRangeType(value);
      raiseSelectionChange(value, rollingPeriod, rollingDateRangeTimePeriodType);
    },
    [raiseSelectionChange, rollingDateRangeTimePeriodType, rollingPeriod]
  );

  const onRollingPeriodChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = Number(e.target.value);
      setRollingPeriod(val);
      raiseSelectionChange(rollingDateRangeType, val, rollingDateRangeTimePeriodType);
    },
    [raiseSelectionChange, rollingDateRangeTimePeriodType, rollingDateRangeType]
  );

  const onRollingDateRangeTimePeriodTypeChange = useCallback(
    (selectedItem?: RollingDateRangeTimePeriodType | null) => {
      let value;
      if (selectedItem) value = selectedItem;
      setRollingDateRangeTimePeriodType(value);
      raiseSelectionChange(rollingDateRangeType, rollingPeriod, value);
    },
    [raiseSelectionChange, rollingDateRangeType, rollingPeriod]
  );

  return (
    <div className="dpc-date-filter-layer__rolling">
      <StringComboBox
        options={Object.keys(RollingDateRangeType)
          .filter((key: string) => isNaN(Number(key)))
          .map((key: string) => {
            return { text: key, value: RollingDateRangeType[key as keyof typeof RollingDateRangeType] };
          })}
        currentValue={rollingDateRangeType}
        onValueChange={onRollingDateRangeTypeChange}
       />
      <input
        type="number"
        min="0"
        className="dpc-date-filter-layer__rolling-days"
        value={rollingPeriod}
        onChange={onRollingPeriodChange}
      />
      <StringComboBox
        options={Object.keys(RollingDateRangeTimePeriodType)
          .filter(
            (key: string) => isNaN(Number(key)) && key !== 'Quarters' /** Removed Quarters option from Rolling Date */
          )
          .map((key: string) => {
            return {
              text: key,
              value: RollingDateRangeTimePeriodType[key as keyof typeof RollingDateRangeTimePeriodType],
            };
          })}
        currentValue={rollingDateRangeTimePeriodType}
        onValueChange={onRollingDateRangeTimePeriodTypeChange}
       />
    </div>
  );
}
